// Shared pieces of the guided Review tab and the guided actions in the Underwriting Module (Jesse, 2026-10-24 strip-back:
// the tab shows the step, the strip, the current step's open items and a compact decision card; every action lives in the
// Underwriting Module). Brand colors only — the doorly-* palette (tailwind.config.js) and the semantic colors in
// constants/statusColors.js. Slate text only on white; navy on the grey ground and light tints (4.5:1).
import React, { useState } from 'react';
import { staffRemainingOnStep, needsOnStep, needOwed, needWording } from '../../../constants/guidedRemaining';

export const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
export const B = Object.freeze({
  ink: '#111827', navy: '#1A3A6B', slate: '#64748B', blue: '#3a6ad9', blueDark: '#2d56b5',
  ground: '#f7f7f7', line: '#e6e6e6', white: '#ffffff', slateLight: '#E7EAF0',
  tan: '#FFDD86', gold: '#C9A84C', cyanLight: '#DCF1FC', green: '#acf44e',
  orange: '#FF7A1A', orangeLight: '#FFEBDC', red: '#ff082f', redLight: '#FFE3EE',
});
export const day = (d) => { const t = d ? new Date(d) : null; return t && !Number.isNaN(t.getTime()) ? t.toLocaleDateString() : null; };
export const stamp = (d) => { const t = d ? new Date(d) : null; return t && !Number.isNaN(t.getTime()) ? t.toLocaleString() : null; };
export const money = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString()}`);
export const nameOf = (a) => [a.firstName, a.lastName].filter(Boolean).join(' ') || `#${a.userId}`;
export const currentStepOf = (a) => Number(a && a.state && a.state.gate ? a.state.gate.currentStep : a && a.state && a.state.step) || 1;

// 🔑 Consent keys in staff words (never "ReadyCheck" or "Phase" — Jesse, 2026-10-24).
const CONSENT_LABELS = { application_step4: 'Application certified (step 3)', readycheck_phase3: 'Credit check authorized (step 7)',
  step7_joint: 'Joint credit check authorized (step 7)' };
export const consentLabel = (k) => CONSENT_LABELS[k] || String(k || '').replace(/readycheck_?/gi, '').replace(/phase\s*\d*/gi, '').replace(/[_:]+/g, ' ').trim() || 'Consent';
export const cleanVersion = (v) => String(v || '').replace(/readycheck[-_ ]?/gi, '').replace(/phase[-_ ]?\d*/gi, '').replace(/^[-_ ]+|[-_ ]+$/g, '') || null;

/** The items still needed on one step for one applicant: the shell's own list on the current step; open needs elsewhere. */
export function itemsForStep(a, n) {
  if (!a || !a.guided) return [];
  if (n === currentStepOf(a)) {
    return staffRemainingOnStep({ state: a.state, needs: a.needs, step5: a.step5, step7: a.step7, letters: a.letters, stepBlockers: a.stepBlockers }).items;
  }
  if (n > currentStepOf(a)) return [];
  return needsOnStep(a.needs, n, a.step5 ? a.step5.formData : null).filter(needOwed)
    .map((x) => ({ key: `need:${x.id}`, borrowerText: needWording(x),
      // staff words (Jesse, 2026-10-26): whose move and what is owed — never the borrower's line as-is
      text: `Waiting on borrower: ${String(needWording(x)).replace(/^./, (c) => c.toLowerCase()).replace(/\.$/, '')} (need #${x.id})`,
      staff: { kind: 'need', needId: x.id, category: x.category || null, docType: x.docType || null, source: x.source || null } }));
}

// A review task's route reason in plain words ("income_provenance:crosscheck_variance_over_tolerance" → "Income
// provenance: crosscheck variance over tolerance").
export const reasonWords = (r) => {
  const s = String(r || '').trim();
  if (!s) return '';
  const [head, ...rest] = s.split(':');
  const words = (x) => x.replace(/[_-]+/g, ' ').trim();
  const out = rest.length ? `${words(head)}: ${words(rest.join(':'))}` : words(head);
  return out.charAt(0).toUpperCase() + out.slice(1);
};

export const btn = (kind) => ({
  minHeight: 36, padding: '0 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
  border: `1px solid ${kind === 'primary' ? B.blue : kind === 'solid' ? B.ink : B.slate}`,
  background: kind === 'solid' ? B.ink : B.white,
  color: kind === 'primary' ? B.blue : kind === 'solid' ? B.white : B.ink,
});

/** A button that asks for a reason in place (≥ 10 characters) before it acts. */
export function ReasonAction({ label, confirmLabel, onConfirm, testid, extra, kind }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const go = async () => {
    if (reason.trim().length < 10) { setErr('The reason needs at least 10 characters.'); return; }
    setBusy(true); setErr(null);
    try { await onConfirm(reason.trim()); setOpen(false); setReason(''); }
    catch (e) { setErr((e.response && e.response.data && (e.response.data.error || e.response.data.message)) || e.message || 'That did not work.'); }
    finally { setBusy(false); }
  };
  if (!open) return <button type="button" data-testid={testid} onClick={() => setOpen(true)} style={btn(kind)}>{label}</button>;
  return (
    <div data-testid={`${testid}-form`} style={{ display: 'grid', gap: 8, marginTop: 6, padding: 10, border: `1px solid ${B.line}`, borderRadius: 8, background: B.ground, width: '100%', boxSizing: 'border-box' }}>
      {extra}
      <label style={{ fontSize: 12, color: B.ink }}>Reason (at least 10 characters, recorded on the file)
        <textarea data-testid={`${testid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
          style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, padding: 8, borderRadius: 6, border: `1px solid ${B.slate}`, boxSizing: 'border-box' }} />
      </label>
      {err && <div role="alert" style={{ fontSize: 12, color: B.red }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-testid={`${testid}-confirm`} disabled={busy} onClick={go} style={btn('solid')}>{busy ? 'Working…' : confirmLabel || label}</button>
        <button type="button" onClick={() => { setOpen(false); setErr(null); }} style={btn()}>Cancel</button>
      </div>
    </div>
  );
}

export function Row({ label, children, testid }) {
  return (
    <div data-testid={testid} style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.5, padding: '2px 0', flexWrap: 'wrap' }}>
      <div style={{ width: 160, flexShrink: 0, color: B.slate }}>{label}</div>
      <div style={{ flex: '1 1 200px', minWidth: 0, color: B.ink, wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

export const Card = ({ children, testid, title, tone }) => (
  <section data-testid={testid} style={{ background: tone === 'orange' ? B.orangeLight : B.white, border: `1px solid ${tone === 'orange' ? B.orange : B.line}`,
    borderRadius: 12, padding: '14px 16px', display: 'grid', gap: 10, minWidth: 0 }}>
    {title && <div style={{ fontSize: 15, fontWeight: 700, color: B.ink }}>{title}</div>}
    {children}
  </section>
);

export const Label = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: B.ink }}>{children}</div>
);
